// ============================================
// COLLISION SYSTEM - 衝突判定
// ============================================

import { getP5 } from '../../core/p5-instance';
import {
    getPlayer,
    getEnemies,
    getBullets,
    getEnemyBullets,
    getShieldActive, setShieldActive,
    setShieldTimer
} from '../../store';
import { triggerPlayerDeath } from '../../features/player/player';
import { handleEnemyDefeat } from '../../features/enemy/enemy';
import {
    createExplosion,
    createHitParticles,
    createHomingHitEffect,
    createArmorDeflectEffect,
    createLaserPenetrateEffect
} from '../../features/particles/particles';

/**
 * checkCollisions() - 衝突判定
 */
export function checkCollisions(): void {
    const player = getPlayer();
    if (!player) {
        return;
    }

    const enemies = getEnemies();
    const bullets = getBullets();
    const enemyBullets = getEnemyBullets();
    const shieldActive = getShieldActive();

    // プレイヤーと敵の衝突判定
    checkPlayerEnemyCollisions(player, enemies, shieldActive);

    // 自機弾と敵の衝突判定
    checkBulletEnemyCollisions(bullets, enemies);

    // 自機弾と敵弾（追尾弾）
    checkBulletEnemyBulletCollisions(bullets, enemyBullets);
}

/**
 * プレイヤーと敵の衝突判定
 */
function checkPlayerEnemyCollisions(
    player: ReturnType<typeof getPlayer>,
    enemies: ReturnType<typeof getEnemies>,
    shieldActive: boolean
): void {
    const p = getP5();

    if (player.state?.name !== 'Normal') {
        return;
    }

    // 無敵時間中は衝突判定をスキップ
    if (player.invincibleTimer && player.invincibleTimer > 0) {
        return;
    }

    for (let i = enemies.length - 1; i >= 0; i--) {
        const enemy = enemies[i];
        const distX = p.abs(player.x - enemy.x);
        const distY = p.abs(player.y - enemy.y);
        if (distX < (player.size / 2 + enemy.size / 2) * 0.7 && distY < (player.size / 2 + enemy.size / 2) * 0.8) {
            if (shieldActive) {
                createExplosion(enemy.x, enemy.y, enemy.color);
                enemies.splice(i, 1);
                setShieldActive(false);
                setShieldTimer(0);
            } else {
                triggerPlayerDeath();
            }
        }
    }
}

/**
 * 自機弾と敵の衝突判定
 */
function checkBulletEnemyCollisions(
    bullets: ReturnType<typeof getBullets>,
    enemies: ReturnType<typeof getEnemies>
): void {
    const p = getP5();

    for (let i = bullets.length - 1; i >= 0; i--) {
        const bullet = bullets[i];
        let bulletHit = false;
        for (let j = enemies.length - 1; j >= 0; j--) {
            const enemy = enemies[j];

            if (bullet.penetrating && bullet.hitEnemies) {
                if (bullet.hitEnemies.find(h => h.enemy === enemy && h.cooldown > 0)) {
                    continue;
                }
            }

            let hit = false;
            if (bullet.isLaser && bullet.laserWidth !== undefined) {
                const closestX = p.constrain(enemy.x, bullet.x - bullet.laserWidth, bullet.x + bullet.laserWidth);
                const closestY = p.constrain(enemy.y, bullet.y - 40, bullet.y + 10);
                hit = p.dist(closestX, closestY, enemy.x, enemy.y) < enemy.size / 2;
            } else {
                hit = p.dist(bullet.x, bullet.y, enemy.x, enemy.y) < enemy.size / 2 + 10;
            }

            if (hit) {
                if (!bullet.isLaser && enemy.type === 'armored' && enemy.armor && p.random() < (enemy.armorChance || 0.2)) {
                    createArmorDeflectEffect(bullet.x, bullet.y, enemy);
                    bullets.splice(i, 1);
                    bulletHit = true;
                    break;
                }
                if (bullet.isHoming) {
                    createHomingHitEffect(bullet.x, bullet.y);
                }

                enemy.hp -= (bullet.damage || 100);
                createHitParticles(bullet.x, bullet.y);

                if (enemy.hp <= 0) {
                    handleEnemyDefeat(enemy);
                    enemies.splice(j, 1);
                }

                if (bullet.penetrating) {
                    if (!bullet.hitEnemies) {
                        bullet.hitEnemies = [];
                    }
                    bullet.hitEnemies.push({ enemy: enemy, cooldown: bullet.hitCooldown || 15 });
                    if (enemy.hp > 0) {
                        createLaserPenetrateEffect(bullet.x, enemy.y);
                    }
                } else {
                    bullets.splice(i, 1);
                    bulletHit = true;
                }
                if (bulletHit) {
                    break;
                }
            }
        }
        if (bullet.penetrating && bullet.hitEnemies) {
            for (const h of bullet.hitEnemies) {
                if (h.cooldown > 0) {
                    h.cooldown--;
                }
            }
        }
    }
}

/**
 * 自機弾と敵弾（追尾弾）の衝突判定
 */
function checkBulletEnemyBulletCollisions(
    bullets: ReturnType<typeof getBullets>,
    enemyBullets: ReturnType<typeof getEnemyBullets>
): void {
    const p = getP5();

    for (let i = bullets.length - 1; i >= 0; i--) {
        const bullet = bullets[i];
        for (let j = enemyBullets.length - 1; j >= 0; j--) {
            const eb = enemyBullets[j];
            if (!eb.homing) {
                continue;
            }
            if (p.dist(bullet.x, bullet.y, eb.x, eb.y) < bullet.size + eb.size) {
                createHitParticles(eb.x, eb.y);
                enemyBullets.splice(j, 1);
                if (!bullet.penetrating) {
                    bullets.splice(i, 1); break;
                }
            }
        }
    }
}
